/* eslint-disable @typescript-eslint/no-explicit-any */

import { Link2, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { threadDisplayTitle } from '../../lib/clinic-logic';
import type { Task, TaskStatusMeta } from '../../lib/task-list';
import type { EmailThreadMetadata, ThreadTask } from '../../types/email';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { TaskPanel } from '../tasks/TaskPanel';

/**
 * Adapts the thread endpoint's `ThreadTask` into the shape `TaskCard` renders.
 *
 * `ThreadTask` has no linked contact, so that field is filled in as null. The assignee no longer
 * needs synthesising — `TaskPanel` resolves `assignedTo` against the real workspace member list,
 * which also means an avatar now renders where the synthetic single-member array used to.
 */
function toCardTask(task: ThreadTask): Task {
  return {
    ...task,
    contact: null,
  } as unknown as Task;
}

export type { ThreadTask };

export interface ThreadTasksPanelProps {
  thread: EmailThreadMetadata;
  tasks: ThreadTask[];
  /** Workspace task statuses, so the cards show the configured label and colour. */
  statuses: TaskStatusMeta[];
  /** The mail conversation these tasks hang off — a task created here is linked to it. */
  conversationId: number;
  workspaceContacts: Array<{ id: number; label: string; typeLabel?: string }>;
  workspaceMembers: Array<{ id: number; email: string; fullName: string | null }>;
  isReadOnly?: boolean;
  /** Host-specific cache invalidation after a task is created here. */
  onTaskCreated?: () => void;
  onTaskClick: (taskId: number) => void;
  onLinkContact: (contactId: number | null) => void;
  onCreateContactClick?: (label: string) => void;
  /** Builds the URL for the linked Contact's detail page. Omit to render its label as plain text. */
  getContactUrl?: (contactId: number) => string;
}

export function ThreadTasksPanel({
  thread,
  tasks,
  statuses,
  conversationId,
  workspaceContacts = [],
  workspaceMembers,
  isReadOnly = false,
  onTaskCreated,
  onTaskClick,
  onLinkContact,
  onCreateContactClick,
  getContactUrl,
}: ThreadTasksPanelProps) {
  const [isLinkingMode, setIsLinkingMode] = useState(false);
  const [contactSearch, setContactSearch] = useState('');

  const filteredContacts = workspaceContacts
    .filter((c) => c.label.toLowerCase().includes(contactSearch.toLowerCase()))
    .slice(0, 5);

  return (
    <div className="p-5 space-y-6">
      <div className="space-y-3">
        <TaskPanel
          tasks={tasks.map(toCardTask)}
          statuses={statuses}
          members={workspaceMembers}
          isReadOnly={isReadOnly}
          title="Linked Tasks"
          onTaskClick={onTaskClick}
          createContext={{ conversationId }}
          createDefaults={{
            // Falls back to the raw subject, so a task can still be created off an unanalysed
            // thread rather than opening pre-filled with "undefined".
            title: thread ? `Task: ${threadDisplayTitle(thread)}` : '',
            description: thread?.coreAsk ?? '',
          }}
          onCreated={onTaskCreated}
          emptyState={
            <div className="rounded-xl border border-dashed border-border p-6 text-center">
              <p className="text-xs italic text-muted-foreground">
                No tasks linked to this thread.
              </p>
            </div>
          }
        />
      </div>

      <div className="pt-4 border-t space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-muted-foreground">Linked Contact Context</h4>
          {!isReadOnly && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-primary font-semibold hover:bg-primary/5 px-2"
              onClick={() => setIsLinkingMode(!isLinkingMode)}
            >
              {isLinkingMode ? 'Cancel' : thread?.contact ? 'Change Link' : 'Link Contact'}
            </Button>
          )}
        </div>

        {isLinkingMode ? (
          <div className="space-y-2 bg-muted border rounded-xl p-3">
            <Label className="text-xs font-semibold">Search Workspace Contacts</Label>
            <div className="relative">
              <Input
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                placeholder="Type to search e.g. Acme Corp..."
                className="h-8 text-xs pr-8"
              />
              {contactSearch && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-6 w-6"
                  onClick={() => setContactSearch('')}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <div className="border rounded-md bg-background divide-y max-h-[180px] overflow-y-auto">
              {filteredContacts.length === 0 && !onCreateContactClick && (
                <p className="text-xs text-muted-foreground p-3 text-center">
                  No matching Contacts found.
                </p>
              )}
              {filteredContacts.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  className="w-full text-left px-3 py-2 text-xs hover:bg-muted transition-colors flex items-center justify-between text-foreground"
                  onClick={() => {
                    onLinkContact(contact.id);
                    setIsLinkingMode(false);
                    setContactSearch('');
                  }}
                >
                  <div className="flex flex-col">
                    <span className="font-semibold">{contact.label}</span>
                    {contact.typeLabel && (
                      <span className="text-[10px] text-muted-foreground">{contact.typeLabel}</span>
                    )}
                  </div>
                  <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              ))}

              {onCreateContactClick && (
                <button
                  type="button"
                  className="w-full text-left px-3 py-2.5 text-xs hover:bg-muted text-muted-foreground font-semibold transition-colors flex items-center gap-1.5"
                  onClick={() => {
                    onCreateContactClick(contactSearch);
                    setIsLinkingMode(false);
                    setContactSearch('');
                  }}
                >
                  <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                  {contactSearch ? `Create Contact: "${contactSearch}"` : 'Create New Contact'}
                </button>
              )}
            </div>
            {thread?.contact && (
              <Button
                variant="ghost"
                className="w-full h-8 text-xs text-foreground hover:text-foreground hover:bg-muted font-semibold border border-dashed border-border"
                onClick={() => {
                  onLinkContact(null);
                  setIsLinkingMode(false);
                }}
              >
                Unlink Current Contact
              </Button>
            )}
          </div>
        ) : thread?.contact ? (
          <Card className="rounded-panel border-border bg-card transition-colors">
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground shrink-0">
                  <Link2 className="h-4 w-4" />
                </div>
                <div className="flex flex-col min-w-0">
                  {getContactUrl ? (
                    <a
                      href={getContactUrl(thread.contact.id)}
                      className="text-xs font-semibold text-foreground line-clamp-1 hover:text-primary hover:underline"
                    >
                      {thread.contact.label}
                    </a>
                  ) : (
                    <span className="text-xs font-semibold text-foreground line-clamp-1">
                      {thread.contact.label}
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground">
                    Auto-matched Contact Context
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-xl border border-dashed border-border p-6 text-center">
            <p className="text-xs text-muted-foreground italic">No Contact profile connected.</p>
            {!isReadOnly && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1.5 mt-3 border-dashed border-border hover:bg-muted"
                onClick={() => setIsLinkingMode(true)}
              >
                <Link2 className="h-3.5 w-3.5" />
                Link to Customer or Vendor
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
